import type { ComponentType, ReactElement } from 'react';
import { AlertTriangle, XCircle } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import type { BitbucketHealthState } from '@kirin/api-client/contracts/bitbucket';

export interface BitbucketTokenHealthBannerProps {
  healthState: BitbucketHealthState;
  tokenExpiresAt?: string | null;
  onUpdateToken?: () => void;
}

type BannerVariant = 'warning' | 'error';

interface BannerContent {
  variant: BannerVariant;
  Icon: ComponentType<{ className?: string }>;
  message: string;
}

function formatDaysUntilExpiry(tokenExpiresAt: string): string | null {
  const now = new Date();
  const expires = new Date(tokenExpiresAt);
  const diffMs = expires.getTime() - now.getTime();
  if (Number.isNaN(diffMs)) return null;
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return null;
  if (diffDays === 1) return '1 day';
  return `${diffDays} days`;
}

function deriveBannerContent(
  healthState: BitbucketHealthState,
  tokenExpiresAt: string | null | undefined
): BannerContent {
  switch (healthState) {
    case 'expiring_soon': {
      const remaining = tokenExpiresAt
        ? formatDaysUntilExpiry(tokenExpiresAt)
        : null;
      return {
        variant: 'warning',
        Icon: AlertTriangle,
        message: remaining
          ? `Token expires in ${remaining}. Rotate the token in Bitbucket and update it here.`
          : 'Token is expiring soon. Rotate the token in Bitbucket and update it here.',
      };
    }
    case 'expired':
      return {
        variant: 'error',
        Icon: XCircle,
        message:
          'Token has expired. Sync is paused. Create a new token in Bitbucket and update it here.',
      };
    case 'invalid_token':
      return {
        variant: 'error',
        Icon: XCircle,
        message:
          'Token is invalid or has been revoked. Create a new token in Bitbucket and update it here.',
      };
    case 'insufficient_permissions':
      return {
        variant: 'error',
        Icon: XCircle,
        message:
          'Token is missing required permissions (Account: Read and Repositories: Read). Re-create the token with the correct permissions.',
      };
    case 'workspace_inaccessible':
      return {
        variant: 'error',
        Icon: XCircle,
        message:
          'Cannot access the connected workspace. The token may have been restricted. Verify the token in Bitbucket.',
      };
    default:
      return {
        variant: 'warning',
        Icon: AlertTriangle,
        message:
          'There may be an issue with your Bitbucket token. Check your settings in Bitbucket.',
      };
  }
}

export function BitbucketTokenHealthBanner({
  healthState,
  tokenExpiresAt,
  onUpdateToken,
}: BitbucketTokenHealthBannerProps): ReactElement {
  const { variant, Icon, message } = deriveBannerContent(
    healthState,
    tokenExpiresAt
  );

  return (
    <Alert
      data-testid="bitbucket-token-health-banner"
      data-state={variant}
      variant={variant === 'error' ? 'destructive' : 'default'}
    >
      <Icon className="h-4 w-4" />
      <AlertDescription className="flex items-center justify-between gap-3">
        <span
          data-testid="bitbucket-token-health-banner-message"
          className="text-sm"
        >
          {message}
        </span>
        {onUpdateToken && (
          <Button
            data-testid="bitbucket-token-health-banner-cta"
            variant="outline"
            size="sm"
            onClick={onUpdateToken}
            className="flex-shrink-0"
          >
            Update Token
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}

BitbucketTokenHealthBanner.displayName = 'BitbucketTokenHealthBanner';
